import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  InputBase,
  Paper,
  Menu,
  MenuItem,
  ListSubheader,
} from '@mui/material';
import { Menu as MenuIcon, Search } from '@mui/icons-material';
import { publicUploadUrl } from '../utils/mediaUrls';
import {
  HEADER_CATEGORY_FALLBACK,
  categoriesForHomeRow1,
  categoriesForHomeRow2,
} from '../utils/homeCategoryRows';

const navButtonSx = {
  color: '#000000',
  textTransform: 'uppercase',
  fontWeight: 'bold',
  fontSize: '14px',
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#72EFD0',
  },
};

const ZarorHeader = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null); // Start with null to prevent flash
  const [logoLoading, setLogoLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [kitAnchorEl, setKitAnchorEl] = useState(null);
  const [trainingAnchorEl, setTrainingAnchorEl] = useState(null);
  const kitCloseTimer = useRef(null);
  const trainingCloseTimer = useRef(null);
  const kitBtnRef = useRef(null);
  const trainingBtnRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  const kitBuilderCategories = useMemo(
    () => categoriesForHomeRow1(categories, catLoading, HEADER_CATEGORY_FALLBACK),
    [categories, catLoading]
  );
  const trainingGearCategories = useMemo(
    () => categoriesForHomeRow2(categories, catLoading),
    [categories, catLoading]
  );

  useEffect(() => {
    let cancelled = false;
    setCatLoading(true);
    axios
      .get(`${apiUrl}/categories`)
      .then((res) => {
        if (!cancelled) setCategories(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setCategories(HEADER_CATEGORY_FALLBACK);
      })
      .finally(() => {
        if (!cancelled) setCatLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  // Fetch logo from settings
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await axios.get(`${apiUrl}/settings`);
        const settings = response.data;
        
        if (settings.site_logo) {
          // Fetch logo media details
          try {
            const mediaResponse = await axios.get(`${apiUrl}/media`);
            const logoMedia = mediaResponse.data.find(m => m.id === parseInt(settings.site_logo));
            if (logoMedia) {
              setLogoUrl(publicUploadUrl(logoMedia.filename));
            } else {
              // No custom logo found, use default
              setLogoUrl('/logo192.png');
            }
          } catch (err) {
            console.error('Error fetching logo media:', err);
            setLogoUrl('/logo192.png');
          }
        } else {
          // No logo set in settings, use default
          setLogoUrl('/logo192.png');
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // Use default logo on error
        setLogoUrl('/logo192.png');
      } finally {
        setLogoLoading(false);
      }
    };

    fetchLogo();
  }, [apiUrl]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const clearCloseTimer = (timerRef) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const openKitMenu = (el) => {
    clearCloseTimer(kitCloseTimer);
    clearCloseTimer(trainingCloseTimer);
    setTrainingAnchorEl(null);
    setKitAnchorEl(el);
  };

  const scheduleCloseKitMenu = () => {
    clearCloseTimer(kitCloseTimer);
    kitCloseTimer.current = setTimeout(() => setKitAnchorEl(null), 160);
  };

  const openTrainingMenu = (el) => {
    clearCloseTimer(trainingCloseTimer);
    clearCloseTimer(kitCloseTimer);
    setKitAnchorEl(null);
    setTrainingAnchorEl(el);
  };

  const scheduleCloseTrainingMenu = () => {
    clearCloseTimer(trainingCloseTimer);
    trainingCloseTimer.current = setTimeout(() => setTrainingAnchorEl(null), 160);
  };

  const mobileMenu = (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={handleDrawerToggle}
      sx={{
        '& .MuiDrawer-paper': { 
          width: 280, 
          boxSizing: 'border-box',
          zIndex: (theme) => theme.zIndex.drawer,
        },
        zIndex: (theme) => theme.zIndex.drawer,
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Zaror Sports
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List>
          <ListItem button component={Link} to="/" onClick={handleDrawerToggle}>
            <ListItemText primary="HOME" />
          </ListItem>
          <ListItem button component={Link} to="/about" onClick={handleDrawerToggle}>
            <ListItemText primary="ABOUT US" />
          </ListItem>
          <ListItem button component={Link} to="/contact" onClick={handleDrawerToggle}>
            <ListItemText primary="CONTACT US" />
          </ListItem>
          <ListSubheader sx={{ bgcolor: 'background.paper', fontWeight: 700 }}>
            Kit Builder
          </ListSubheader>
          {kitBuilderCategories.map((c) => (
            <ListItem
              button
              key={`kit-${c.id}`}
              component={Link}
              to={`/${c.slug}`}
              onClick={handleDrawerToggle}
              sx={{ pl: 4 }}
            >
              <ListItemText primary={c.name} />
            </ListItem>
          ))}
          <ListSubheader sx={{ bgcolor: 'background.paper', fontWeight: 700 }}>
            Training Gears
          </ListSubheader>
          {trainingGearCategories.length > 0 ? (
            trainingGearCategories.map((c) => (
              <ListItem
                button
                key={`train-${c.id}`}
                component={Link}
                to={`/${c.slug}`}
                onClick={handleDrawerToggle}
                sx={{ pl: 4 }}
              >
                <ListItemText primary={c.name} />
              </ListItem>
            ))
          ) : (
            <ListItem sx={{ pl: 4 }}>
              <ListItemText primary="None yet — add categories to More Sports in admin" />
            </ListItem>
          )}
          <ListItem button component={Link} to="/tracksuits" onClick={handleDrawerToggle}>
            <ListItemText primary="TRACKSUITS" />
          </ListItem>
          <ListItem button component={Link} to="/privacy-policy" onClick={handleDrawerToggle}>
            <ListItemText primary="PRIVACY POLICY" />
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        color="default" 
        elevation={0}
        sx={{ 
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              py: 1,
              ...(isMobile && {
                justifyContent: 'space-between',
                alignItems: 'center',
              }),
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                ...(isMobile && { justifyContent: 'flex-start' }),
              }}
            >
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {logoUrl && (
                    <img
                      src={logoUrl}
                      alt="Zaror Sports Logo"
                      style={{
                        height: isMobile ? '52px' : '60px',
                        width: 'auto',
                        marginRight: isMobile ? 0 : '16px',
                        opacity: logoLoading ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                      }}
                      onError={(e) => {
                        e.target.src = '/logo192.png';
                      }}
                    />
                  )}
                </Box>
              </Link>
            </Box>

            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{
                  color: '#000000',
                  zIndex: (theme) => theme.zIndex.appBar + 1,
                  position: 'relative',
                  flexShrink: 0,
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <Button component={Link} to="/" sx={navButtonSx}>
                  HOME
                </Button>
                <Button component={Link} to="/about" sx={navButtonSx}>
                  ABOUT US
                </Button>
                <Button component={Link} to="/contact" sx={navButtonSx}>
                  CONTACT US
                </Button>
                <Box
                  onMouseEnter={() => kitBtnRef.current && openKitMenu(kitBtnRef.current)}
                  onMouseLeave={scheduleCloseKitMenu}
                  sx={{ display: 'inline-block' }}
                >
                  <Button
                    ref={kitBtnRef}
                    sx={navButtonSx}
                    aria-haspopup="true"
                    aria-expanded={Boolean(kitAnchorEl)}
                    aria-controls="kit-builder-menu"
                  >
                    Kit Builder
                  </Button>
                  <Menu
                    id="kit-builder-menu"
                    anchorEl={kitAnchorEl}
                    open={Boolean(kitAnchorEl)}
                    onClose={() => setKitAnchorEl(null)}
                    MenuListProps={{
                      onMouseEnter: () => clearCloseTimer(kitCloseTimer),
                      onMouseLeave: scheduleCloseKitMenu,
                      dense: true,
                      sx: { py: 0 },
                    }}
                    PaperProps={{
                      onMouseEnter: () => clearCloseTimer(kitCloseTimer),
                      onMouseLeave: scheduleCloseKitMenu,
                    }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    disableAutoFocusItem
                    keepMounted
                  >
                    {kitBuilderCategories.length === 0 ? (
                      <MenuItem disabled>No categories</MenuItem>
                    ) : (
                      kitBuilderCategories.map((c) => (
                        <MenuItem
                          key={c.id}
                          component={Link}
                          to={`/${c.slug}`}
                          onClick={() => setKitAnchorEl(null)}
                        >
                          {c.name}
                        </MenuItem>
                      ))
                    )}
                  </Menu>
                </Box>
                <Box
                  onMouseEnter={() => trainingBtnRef.current && openTrainingMenu(trainingBtnRef.current)}
                  onMouseLeave={scheduleCloseTrainingMenu}
                  sx={{ display: 'inline-block' }}
                >
                  <Button
                    ref={trainingBtnRef}
                    sx={navButtonSx}
                    aria-haspopup="true"
                    aria-expanded={Boolean(trainingAnchorEl)}
                    aria-controls="training-gears-menu"
                  >
                    Training Gears
                  </Button>
                  <Menu
                    id="training-gears-menu"
                    anchorEl={trainingAnchorEl}
                    open={Boolean(trainingAnchorEl)}
                    onClose={() => setTrainingAnchorEl(null)}
                    MenuListProps={{
                      onMouseEnter: () => clearCloseTimer(trainingCloseTimer),
                      onMouseLeave: scheduleCloseTrainingMenu,
                      dense: true,
                      sx: { py: 0 },
                    }}
                    PaperProps={{
                      onMouseEnter: () => clearCloseTimer(trainingCloseTimer),
                      onMouseLeave: scheduleCloseTrainingMenu,
                    }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    disableAutoFocusItem
                    keepMounted
                  >
                    {trainingGearCategories.length === 0 ? (
                      <MenuItem disabled>No sports in More Sports yet</MenuItem>
                    ) : (
                      trainingGearCategories.map((c) => (
                        <MenuItem
                          key={c.id}
                          component={Link}
                          to={`/${c.slug}`}
                          onClick={() => setTrainingAnchorEl(null)}
                        >
                          {c.name}
                        </MenuItem>
                      ))
                    )}
                  </Menu>
                </Box>
                <Button component={Link} to="/tracksuits" sx={navButtonSx}>
                  TRACKSUITS
                </Button>
                <Button component={Link} to="/privacy-policy" sx={navButtonSx}>
                  PRIVACY POLICY
                </Button>
              </Box>
            )}

            {/* Search */}
            {!isMobile && (
              <Box sx={{ ml: 4 }}>
                <Paper
                  component="form"
                  sx={{
                    p: '2px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    width: 200,
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none',
                  }}
                >
                  <InputBase
                    sx={{ ml: 1, flex: 1 }}
                    placeholder="Search"
                    inputProps={{ 'aria-label': 'search' }}
                  />
                  <IconButton
                    type="submit"
                    sx={{ p: '10px' }}
                    aria-label="search"
                  >
                    <Search />
                  </IconButton>
                </Paper>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      {mobileMenu}
    </>
  );
};

export default ZarorHeader;

